// PLAN-cockpit-v2-iter1 Phase 1 + Phase 7 (2026-05-11): Graph Lens
// Domain filter を Sub-sidebar に移動 (Shell 統一、Phase 2-D 残)
// sessionStorage + event で Sub-sidebar (Sidebar) ↔ Main の state を同期
// (Out of MVP: focus mode、cluster filter、centrality 表示、search box)

import { useEffect, useMemo, useRef, useState } from 'react'
import { Network } from 'vis-network'
import { DataSet } from 'vis-data'
import { getGraph, type GraphData, type GraphNode } from '../../api/kdb'
import { useSelectedItem, type SelectedDetailItem } from '../hooks/useSelectedItem'
import { EmptyState } from '../components/common/EmptyState'

// graph.json contract (spec/components/sync-graphify/graph-json-contract.md):
// graphify の node id は UUID とは限らず (concept_kdb / e_<partial>_<slug> / art_<8hex> 等)、
// そのまま /api/entries/:id に渡すと 400 invalid_uuid になる。KDB item は kdb_id か source_file の
// UUID で解決し、concept/paper 等の抽象ノードは detail を持たない。
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i
const CONCEPT_ID_RE = /^(concept|paper|plan|claude_md|doc|external)/i

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

// クリックされた node を KDB item (entry/article) に解決する。KDB backing が無ければ null。
function resolveKdbItem(n: GraphNode): SelectedDetailItem | null {
  const kind = asString(n.kind)
  const sourceFile = asString(n.source_file)
  const itemTypeFromSource = (): 'entry' | 'article' =>
    kind === 'article' || n.origin === 'synthesized' || n.type === 'article' || sourceFile?.startsWith('articles/')
      ? 'article' : 'entry'

  // 1) 明示 kdb_id (graphify が出力するようになった場合の正式キー)
  const kdbMatch = asString(n.kdb_id)?.match(UUID_RE)
  if (kdbMatch) {
    return { id: kdbMatch[0], itemType: itemTypeFromSource() }
  }
  // 2) concept/abstract ノードは KDB detail を持たない
  if ((kind && ['concept', 'paper', 'doc', 'external'].includes(kind)) || CONCEPT_ID_RE.test(n.id)) {
    return null
  }
  // 3) 移行 fallback: source_file から UUID を抽出
  const m = sourceFile?.match(UUID_RE)
  if (!m) return null
  return { id: m[0], itemType: itemTypeFromSource() }
}

const DOMAIN_COLORS: Record<string, string> = {
  finance: '#3F6E5C',
  health: '#B8633E',
  mind: '#5B5A8C',
  relation: '#8B6F3D',
  'science-tech': '#2A6F97',  // 寒色系 (青) で技術・科学を表現、sensitive ではない
  general: '#6B6B6B',
}

const ACTIVE_DOMAINS = ['finance', 'health', 'mind', 'relation', 'science-tech', 'general']
const STORAGE_KEY = 'cockpit_v2.graph_domains'
const EVENT_NAME = 'graph_domains_change'

function readDomains(): Set<string> {
  try {
    const v = window.sessionStorage.getItem(STORAGE_KEY)
    if (!v) return new Set(ACTIVE_DOMAINS)
    const arr = JSON.parse(v) as string[]
    return new Set(arr.filter(d => ACTIVE_DOMAINS.includes(d)))
  } catch { return new Set(ACTIVE_DOMAINS) }
}
function writeDomains(s: Set<string>) {
  try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(s))) } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(EVENT_NAME))
}

// ===== GraphSidebar =====

export function GraphSidebar() {
  const [selected, setSelected] = useState<Set<string>>(readDomains)

  useEffect(() => {
    const handler = () => setSelected(readDomains())
    window.addEventListener(EVENT_NAME, handler)
    return () => window.removeEventListener(EVENT_NAME, handler)
  }, [])

  function toggle(d: string) {
    const next = new Set(selected)
    if (next.has(d)) next.delete(d)
    else next.add(d)
    setSelected(next)
    writeDomains(next)
  }
  function selectAll() { const all = new Set(ACTIVE_DOMAINS); setSelected(all); writeDomains(all) }
  function clearAll() { const empty = new Set<string>(); setSelected(empty); writeDomains(empty) }

  return (
    <div className="p-4 space-y-4 text-sm">
      <div>
        <h2 className="text-xs uppercase tracking-wide text-text-subtle mb-3">Filters</h2>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs uppercase tracking-wide text-text-subtle">Domain</h3>
              <div className="flex gap-2 text-[10px] text-text-subtle">
                <button type="button" onClick={selectAll} className="hover:text-accent-primary">All</button>
                <span>·</span>
                <button type="button" onClick={clearAll} className="hover:text-accent-primary">None</button>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              {ACTIVE_DOMAINS.map(d => (
                <label key={d} className="flex items-center gap-2 cursor-pointer px-2 py-1 rounded hover:bg-bg-overlay">
                  <input
                    type="checkbox"
                    checked={selected.has(d)}
                    onChange={() => toggle(d)}
                    className="accent-accent-primary"
                  />
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: DOMAIN_COLORS[d] }} aria-hidden />
                  <span className="capitalize text-text-base">{d}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-text-subtle pt-3 border-t border-border-subtle">
        focus mode / cluster filter / centrality / search は今後の Plan で追加予定。
      </p>
    </div>
  )
}

// ===== GraphLens =====

export function GraphLens() {
  const { setSelectedItem } = useSelectedItem()
  const containerRef = useRef<HTMLDivElement>(null)
  const networkRef = useRef<Network | null>(null)
  const [data, setData] = useState<GraphData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(readDomains)
  // concept/抽象ノード (KDB Entry を持たない) をクリックした際の軽い注記
  const [conceptNote, setConceptNote] = useState<string | null>(null)

  // Sub-sidebar からの変更を listen
  useEffect(() => {
    const handler = () => setSelectedDomains(readDomains())
    window.addEventListener(EVENT_NAME, handler)
    return () => window.removeEventListener(EVENT_NAME, handler)
  }, [])

  // graph.json fetch
  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    getGraph(ac.signal)
      .then((g) => setData(g))
      .catch((e: unknown) => {
        if ((e as { name?: string }).name === 'AbortError') return
        setError(e instanceof Error ? e.message : 'Failed to load graph')
      })
      .finally(() => setLoading(false))
    return () => ac.abort()
  }, [])

  // filter 適用後の nodes / edges
  const filtered = useMemo(() => {
    if (!data) return null
    const nodes = data.nodes.filter((n) => {
      const d = (n as { domain?: string }).domain ?? 'general'
      return selectedDomains.has(d)
    })
    const visibleIds = new Set(nodes.map((n) => n.id))
    const links = data.links.filter((l) => visibleIds.has(l.source) && visibleIds.has(l.target))
    return { nodes, links }
  }, [data, selectedDomains])

  // vis-network 描画
  useEffect(() => {
    if (!containerRef.current || !filtered) return
    const visNodes = new DataSet(
      filtered.nodes.map((n) => {
        const d = (n as { domain?: string }).domain ?? 'general'
        const isArticle = n.origin === 'synthesized' || n.type === 'article'
        return {
          id: n.id,
          label: n.title ?? n.id.slice(0, 8),
          color: { background: DOMAIN_COLORS[d] ?? DOMAIN_COLORS.general, border: 'transparent' },
          shape: isArticle ? 'diamond' : 'dot',
          size: 10,
          font: { size: 11, color: '#666' },
        }
      })
    )
    const visEdges = new DataSet(
      filtered.links.map((l, i) => ({
        id: `e${i}`,
        from: l.source,
        to: l.target,
        color: { color: '#C4C0BB', opacity: 0.4 },
        width: 1,
        smooth: false as const,
      }))
    )

    const network = new Network(
      containerRef.current,
      { nodes: visNodes, edges: visEdges },
      {
        physics: { enabled: true, stabilization: { iterations: 200 } },
        interaction: { hover: true, dragNodes: true, zoomView: true },
        nodes: { borderWidth: 0 },
        edges: { arrows: { to: { enabled: true, scaleFactor: 0.4 } } },
      }
    )

    network.once('stabilizationIterationsDone', () => {
      network.setOptions({ physics: { enabled: false } })
    })

    network.on('selectNode', (e) => {
      const id = (e as { nodes: string[] }).nodes[0]
      if (!id) return
      const n = filtered.nodes.find((x) => x.id === id)
      if (!n) return
      // node id は UUID とは限らないため kdb_id / source_file から実 item を解決する
      const resolved = resolveKdbItem(n)
      if (resolved) {
        setConceptNote(null)
        setSelectedItem(resolved)
      } else {
        // concept/抽象ノード: 壊れた fetch を投げず、注記だけ出す
        setSelectedItem(null)
        setConceptNote(n.title ?? n.id)
      }
    })

    networkRef.current = network
    return () => {
      network.destroy()
      networkRef.current = null
    }
  }, [filtered, setSelectedItem])

  return (
    <div className="h-full flex flex-col">
      {/* Header: count summary only (Domain filter は Sub-sidebar、Phase 7 で移動) */}
      <header className="border-b border-border-subtle bg-bg-surface px-4 py-2 flex items-center justify-between text-xs">
        <span className="uppercase tracking-wide text-text-subtle">Knowledge graph</span>
        {data && (
          <span className="text-text-subtle">
            {filtered?.nodes.length ?? 0} / {data.nodes.length} nodes · {filtered?.links.length ?? 0} edges
          </span>
        )}
      </header>

      {/* Graph canvas */}
      <div className="flex-1 relative bg-bg-base">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-text-subtle z-10 pointer-events-none">
            Loading graph (vis-network ~400KB)…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <EmptyState title="Failed to load graph" description={error} />
          </div>
        )}
        <div ref={containerRef} className="w-full h-full" />
        {conceptNote && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 max-w-[90%] rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-xs text-text-subtle shadow-sm flex items-center gap-2">
            <span className="truncate">
              <span className="text-text-base">{conceptNote}</span> — 概念ノードです (対応する KDB Entry はありません)
            </span>
            <button
              type="button"
              onClick={() => setConceptNote(null)}
              className="shrink-0 text-text-subtle hover:text-accent-primary"
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
// GraphNodeDetail は AppShell の SharedDetailPanel に統合済 (iter1 Phase 1)
